import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Disclaimer } from './components';
import { useTryOnDeps } from './context';
import { useCreateRender, useRender } from './queries';
import type { NailArtDesign, Render } from './schemas';
import { TRYON_NAMESPACE } from './strings';

/**
 * BP-22 §6: the photo tab.
 *
 * **It is the screen rule 5 already points at.** BP-21's live view offers
 * photo mode when the device cannot keep up; this is what it offers. The
 * same shade strip sits above both, because the choice she is making is
 * the colour and not the technology.
 *
 * **The disclaimer is on this screen *and* in the picture.** Rule 2 bakes
 * two lines into the pixels because the file is what gets shared; the
 * screen says them too because she reads the screen before she decides
 * whether to share anything. Neither is redundant.
 *
 * **Rule 1 is said out loud.** "Deleted within a day" is a promise, and a
 * promise she cannot read before she uploads is not one she agreed to.
 */
export function PhotoTab({
  shadeSkuId,
  design,
  sessionId,
  onClearDesign,
}: {
  shadeSkuId?: string;
  design?: NailArtDesign;
  sessionId?: string;
  onClearDesign?: () => void;
}): JSX.Element {
  const { t } = useTranslation(TRYON_NAMESPACE);
  const { shareImage, outputUrl } = useTryOnDeps();
  const create = useCreateRender();
  const [renderId, setRenderId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const render = useRender(renderId);

  const choose = (file: File | undefined): void => {
    if (file === undefined) {
      return;
    }
    create.mutate(
      {
        photo: file,
        options: {
          ...(shadeSkuId === undefined ? {} : { shadeSkuId }),
          ...(design === undefined ? {} : { designId: design.id }),
          ...(sessionId === undefined ? {} : { sessionId }),
        },
      },
      {
        onSuccess: (queued: Render) => {
          setRenderId(queued.id);
        },
      },
    );
  };

  const current = render.data;
  // `output_key` is only read as "is there a picture": the URL is built
  // from the id, and the storage key never leaves the server's own
  // vocabulary — a path on a client's screen is one somebody will
  // eventually try to edit.
  const url = current === undefined || current.output_key === null ? null : outputUrl(current.id);

  return (
    <section data-testid="photo-tab" className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">{t('photo.hint')}</p>
      <p className="text-xs text-muted-foreground" data-testid="photo-temporary">
        {t('photo.temporary')}
      </p>

      {design !== undefined && (
        <p className="text-xs" data-testid="photo-design">
          {design.name}
          {onClearDesign !== undefined && (
            <button
              type="button"
              data-testid="photo-design-clear"
              className="ml-2 text-muted-foreground underline"
              onClick={onClearDesign}
            >
              {t('designs.clear')}
            </button>
          )}
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        data-testid="photo-input"
        className="hidden"
        onChange={(event) => {
          choose(event.target.files?.[0]);
        }}
      />
      <button
        type="button"
        data-testid="photo-choose"
        className="self-start rounded bg-primary px-3 py-2 text-sm text-primary-foreground"
        onClick={() => {
          // The `<input type="file">` is hidden and this button is what
          // she taps: a styled label would do the same, but a label
          // cannot say "Try another" on the second press.
          input.current?.click();
        }}
      >
        {renderId === null ? t('photo.choose') : t('photo.again')}
      </button>

      {current !== undefined && current.status !== 'done' && current.status !== 'failed' && (
        <p role="status" data-testid="photo-working">
          {t('photo.working')}
        </p>
      )}

      {current?.status === 'failed' && current.failure !== null && (
        <p role="status" data-testid={`photo-failed-${current.failure}`} className="text-sm">
          {t(`photo.failed.${current.failure}`)}
        </p>
      )}

      {current?.status === 'expired' && (
        <p role="status" data-testid="photo-expired" className="text-sm">
          {t('photo.expired')}
        </p>
      )}

      {current?.status === 'done' && url !== null && (
        <figure className="flex flex-col gap-2" data-testid="photo-result">
          <img src={url} alt={t('photo.ready')} className="w-full rounded" />
          {shareImage !== undefined && (
            <button
              type="button"
              data-testid="photo-share"
              className="self-start rounded border px-3 py-1 text-sm"
              onClick={() => {
                void shareImage(url, t('title'));
              }}
            >
              {t('photo.share')}
            </button>
          )}
        </figure>
      )}

      <Disclaimer />
    </section>
  );
}
